'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const cobalt = 'rgb(43, 98, 239)';
const cobaltGlow = 'rgb(85, 136, 251)';
const silverText = 'oklch(95% .005 265)';
const silverMuted = 'oklch(65% .01 265)';
const obsidian = 'rgb(8, 11, 18)';
const graphite = 'rgb(19, 22, 28)';
const steel = 'rgb(31, 34, 39)';

const INQUIRY_TYPES = [
  'Translation & Localization',
  'AI Training Data',
  'LLM Evaluation & RLHF',
    'Multilingual AI Safety Testing',
  'Multilingual AI Evaluation',
  'Voice & Audio Services',
  'Enterprise Partnership',
  'General Inquiry',
];

interface FormState {
  name: string;
  email: string;
  company: string;
  phone: string;
  inquiryType: string;
  message: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  email: '',
  company: '',
  phone: '',
  inquiryType: '',
  message: '',
};

export default function ContactPage() {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg('');

    try {
      const res = await fetch('/api/submit-contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      const data = await res.json() as { success?: boolean; error?: string };

      if (!res.ok || !data.success) {
        setErrorMsg(data.error || 'Something went wrong. Please try again.');
        setStatus('error');
        return;
      }

      setStatus('success');
      setForm(EMPTY_FORM);
    } catch {
      setErrorMsg('Network error. Please check your connection and try again.');
      setStatus('error');
    }
  };

  const inputClass = `w-full px-4 py-3 rounded-lg text-sm outline-none transition-all border focus:border-[rgb(43,98,239)] focus:ring-1 focus:ring-[rgb(43,98,239)]`;
  const inputStyle = {
    backgroundColor: 'rgb(13, 16, 23)',
    borderColor: steel,
    color: silverText,
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: obsidian }}>
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 overflow-hidden">
        {/* Background glow */}
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full blur-[120px] opacity-10 pointer-events-none"
          style={{ background: `radial-gradient(ellipse, ${cobalt}, transparent 70%)` }}
        />
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 relative z-10">
          <div className="max-w-2xl">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium mb-6 border"
              style={{ backgroundColor: 'rgba(43,98,239,0.08)', borderColor: 'rgba(43,98,239,0.25)', color: cobaltGlow }}
            >
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: cobaltGlow }} />
              Business Inquiries
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-5 leading-tight" style={{ color: silverText }}>
              Let&apos;s talk about<br />
              <span style={{ color: cobaltGlow }}>your project</span>
            </h1>
            <p className="text-base lg:text-lg leading-relaxed" style={{ color: silverMuted }}>
              Whether you need multilingual AI data, enterprise translation, or a custom language operations program - our team is ready to scope a solution for you.
            </p>
          </div>
        </div>
      </section>

      {/* Main content */}
      <section className="pb-24">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">

            {/* Left: contact info */}
            <div className="lg:col-span-2 space-y-8">
              {/* Contact details */}
              <div
                className="rounded-2xl p-6 border"
                style={{ backgroundColor: graphite, borderColor: steel }}
              >
                <h2 className="text-base font-semibold mb-5" style={{ color: silverText }}>Get in touch</h2>
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ backgroundColor: 'rgba(43,98,239,0.12)' }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cobaltGlow} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect width="20" height="16" x="2" y="4" rx="2" />
                        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs font-medium mb-0.5" style={{ color: silverMuted }}>Email</p>
                      <a href="mailto:sales@into23.com" className="text-sm font-medium hover:underline" style={{ color: silverText }}>
                        sales@into23.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ backgroundColor: 'rgba(43,98,239,0.12)' }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cobaltGlow} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs font-medium mb-0.5" style={{ color: silverMuted }}>Headquarters</p>
                      <p className="text-sm font-medium" style={{ color: silverText }}>Hong Kong SAR</p>
                      <p className="text-xs mt-0.5" style={{ color: silverMuted }}>With delivery teams across APAC</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ backgroundColor: 'rgba(43,98,239,0.12)' }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cobaltGlow} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs font-medium mb-0.5" style={{ color: silverMuted }}>Response time</p>
                      <p className="text-sm font-medium" style={{ color: silverText }}>Within 1 business day</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* What to expect */}
              <div
                className="rounded-2xl p-6 border"
                style={{ backgroundColor: graphite, borderColor: steel }}
              >
                <h2 className="text-base font-semibold mb-4" style={{ color: silverText }}>What happens next</h2>
                <ol className="space-y-4">
                  {[
                    { step: '01', title: 'We review your inquiry', desc: 'Our team reads every submission and routes it to the right specialist.' },
                    { step: '02', title: 'Discovery call', desc: 'We schedule a brief call to understand your scope, timeline, and goals.' },
                    { step: '03', title: 'Tailored proposal', desc: 'You receive a scoped proposal with pricing and delivery timeline.' },
                  ].map((item) => (
                    <li key={item.step} className="flex gap-4">
                      <span
                        className="text-xs font-bold tabular-nums flex-shrink-0 mt-0.5"
                        style={{ color: cobaltGlow }}
                      >
                        {item.step}
                      </span>
                      <div>
                        <p className="text-sm font-semibold" style={{ color: silverText }}>{item.title}</p>
                        <p className="text-xs mt-0.5 leading-relaxed" style={{ color: silverMuted }}>{item.desc}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Quick links */}
              <div className="flex flex-col gap-2">
                <Link href="/get-a-quote">
                  <div
                    className="flex items-center justify-between px-4 py-3 rounded-xl border text-sm font-medium transition-colors hover:border-[rgb(43,98,239)]"
                    style={{ backgroundColor: graphite, borderColor: steel, color: silverText }}
                  >
                    Need a price estimate?
                    <span style={{ color: cobaltGlow }}>Get a Quote →</span>
                  </div>
                </Link>
                <Link href="/services">
                  <div
                    className="flex items-center justify-between px-4 py-3 rounded-xl border text-sm font-medium transition-colors hover:border-[rgb(43,98,239)]"
                    style={{ backgroundColor: graphite, borderColor: steel, color: silverText }}
                  >
                    Explore our services
                    <span style={{ color: cobaltGlow }}>View all →</span>
                  </div>
                </Link>
              </div>
            </div>

            {/* Right: form */}
            <div className="lg:col-span-3">
              <div
                className="rounded-2xl p-8 border"
                style={{ backgroundColor: graphite, borderColor: steel }}
              >
                {status === 'success' ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
                      style={{ backgroundColor: 'rgba(43,98,239,0.12)' }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={cobaltGlow} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold mb-3" style={{ color: silverText }}>Message sent</h3>
                    <p className="text-sm leading-relaxed max-w-sm" style={{ color: silverMuted }}>
                      Thank you for reaching out. Our team will review your inquiry and get back to you within one business day.
                    </p>
                    <button
                      className="mt-8 px-6 py-2.5 rounded-lg text-sm font-semibold text-white transition-opacity hover:opacity-90"
                      style={{ backgroundColor: cobalt }}
                      onClick={() => setStatus('idle')}
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <>
                    <h2 className="text-lg font-bold mb-6" style={{ color: silverText }}>Business inquiry form</h2>
                    <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                      {/* Name + Email */}
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium mb-1.5" style={{ color: silverMuted }}>
                            Full name <span style={{ color: cobaltGlow }}>*</span>
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            required
                            placeholder="Jane Smith"
                            className={inputClass}
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1.5" style={{ color: silverMuted }}>
                            Work email <span style={{ color: cobaltGlow }}>*</span>
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            required
                            placeholder="jane@company.com"
                            className={inputClass}
                            style={inputStyle}
                          />
                        </div>
                      </div>

                      {/* Company + Phone */}
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium mb-1.5" style={{ color: silverMuted }}>
                            Company
                          </label>
                          <input
                            type="text"
                            name="company"
                            value={form.company}
                            onChange={handleChange}
                            placeholder="Acme Corp"
                            className={inputClass}
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1.5" style={{ color: silverMuted }}>
                            Phone number
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={form.phone}
                            onChange={handleChange}
                            placeholder="+1 (555) 000-0000"
                            className={inputClass}
                            style={inputStyle}
                          />
                        </div>
                      </div>

                      {/* Inquiry type */}
                      <div>
                        <label className="block text-xs font-medium mb-1.5" style={{ color: silverMuted }}>
                          Inquiry type
                        </label>
                        <select
                          name="inquiryType"
                          value={form.inquiryType}
                          onChange={handleChange}
                          className={inputClass}
                          style={{ ...inputStyle, appearance: 'none' }}
                        >
                          <option value="">Select a topic…</option>
                          {INQUIRY_TYPES.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>

                      {/* Message */}
                      <div>
                        <label className="block text-xs font-medium mb-1.5" style={{ color: silverMuted }}>
                          Message <span style={{ color: cobaltGlow }}>*</span>
                        </label>
                        <textarea
                          name="message"
                          value={form.message}
                          onChange={handleChange}
                          required
                          rows={5}
                          placeholder="Tell us about your project, languages needed, volume, and any specific requirements…"
                          className={`${inputClass} resize-none`}
                          style={inputStyle}
                        />
                      </div>

                      {/* Error */}
                      {status === 'error' && errorMsg && (
                        <div
                          className="flex items-start gap-2.5 px-4 py-3 rounded-lg border text-sm"
                          style={{ backgroundColor: 'rgba(239,68,68,0.08)', borderColor: 'rgba(239,68,68,0.25)', color: '#f87171' }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" x2="12" y1="8" y2="12" />
                            <line x1="12" x2="12.01" y1="16" y2="16" />
                          </svg>
                          {errorMsg}
                        </div>
                      )}

                      {/* Submit */}
                      <button
                        type="submit"
                        disabled={status === 'submitting'}
                        className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg text-sm font-semibold text-white transition-all disabled:opacity-60"
                        style={{ backgroundColor: cobalt }}
                      >
                        {status === 'submitting' ? (
                          <>
                            <svg className="animate-spin" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                            </svg>
                            Sending…
                          </>
                        ) : (
                          <>
                            Send message
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="m22 2-7 20-4-9-9-4Z" />
                              <path d="M22 2 11 13" />
                            </svg>
                          </>
                        )}
                      </button>

                      <p className="text-xs text-center" style={{ color: 'oklch(50% .01 265)' }}>
                        By submitting this form you agree to our{' '}
                        <Link href="/privacy-policy" className="underline hover:opacity-80" style={{ color: silverMuted }}>Privacy Policy</Link>.
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
